import { Box, Grid2 } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useGet from "../../../services/hooks/useGet";
import usePost from "../../../services/hooks/usePost";
import usePut from "../../../services/hooks/usePut";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import { UserRoles } from "../../../shared/constants/enums";
import { Heading, Input } from "../../../shared/ReusableComponents";
import CheckboxInput from "../../../shared/ReusableComponents/Checkbox";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { FluidOrderItem } from "./FluidOrderItem";

interface IFluidOrderItems {
  indication: string;
  fluidType: string;
  volume: any;
  additiveAmount: string;
  rate: any;
  route: string;
  prescribedBy: any;
}
const style = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-disabled": {
      backgroundColor: "#F5F5F5",
      "& fieldset": {
        borderColor: "#B1B1B1",
      },
    },
    "&:hover": {
      "&.Mui-disabled": {
        backgroundColor: "#F5F5F5", // Prevent hover background change on disabled input
        "& fieldset": {
          borderColor: "#B1B1B1", // Prevent hover border color change on disabled input
        },
      },
    },
  },
};

export const AdminFluidOrder = ({ templateId }: { templateId: any }) => {
  const comorbiditesList = [
    {
      label: "Heart Failure",
      key: "heartFailure",
    },
    {
      label: "Kidney Failure",
      key: "kidneyFailure",
    },
    {
      label: "Liver Failure",
      key: "liverFailure",
    },
    { label: "Diabetes", key: "diabetes" },
    { label: "Nil", key: "nil" },
    { label: "Other", key: "other" },
  ];
  const [comorbidities, setComorbidities] = useState<any>({
    heartFailure: false,
    kidneyFailure: false,
    liverFailure: false,
    diabetes: false,
    nil: false,
    other: false,
  });
  const [instructions, setInstructions] = useState<string>("");
  const [otherComborbidity, setOtherComborbidity] = useState<string>("");
  const [fluidRestriction, setFluidRestriction] = useState<string>("");

  const [nilSelected, setNilSelected] = useState(false);
  const [originalData, setOriginalData] = useState<any>(null);

  const fields = [
    { label: "Indication", key: "indication" },
    { label: "Fluid Type", key: "fluidType" },
    { label: "Volume (mL)", key: "volume" },
    { label: "Additive and Amount", key: "additiveAmount" },
    { label: "Rate (mL/hr)", key: "rate" },
    { label: "Route", key: "route" },
    { label: "Prescribed By", key: "prescribedBy" },
  ];

  const [offsetList, setOffsetList] = useState<(number | undefined)[]>([
    undefined,
  ]);
  const [offsetErrors, setOffsetErrors] = useState<string[]>([""]);
  const [formErrorsList, setFormErrorsList] = useState<
    Array<{ [key: string]: boolean }>
  >([{}]);
  const maxItems = 10;
  const [isEditMode, setIsEditMode] = useState<boolean>(true);
  const [isCreated, setIsCreated] = useState<boolean>(false);
  const auth = useSelector((state: any) => state.login);
  const isCoordinate = auth?.userInfo?.roleId === UserRoles.SUBJECT_COORDINATOR;

  const generateInitialForm = () => ({
    fluidOrderId: undefined,
    indication: "",
    fluidType: "",
    volume: null,
    additiveAmount: "",
    rate: null,
    route: "",
    prescribedBy: CommonFunctions.formatFirstAndLastName(
      auth?.userInfo?.userFirstName,
      auth?.userInfo?.userLastName,
      { prefixKey: "doctor" }
    ),
  });
  const [formDataList, setFormDataList] = useState<IFluidOrderItems[] | any[]>([
    generateInitialForm(),
  ]);

  const { postService, data: postRes } = usePost();
  const {
    getService,
    loading: getFluidLoading,
    data: getFluidResponse,
  } = useGet();
  const { putService, data: putRes } = usePut();
  const dispatch = useDispatch();

  const isValueEmpty = (value: any): boolean => {
    return (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    );
  };

  const isCurrentRowValid = (rowData: any) => {
    return fields.every(({ key }) => {
      if (["additiveAmount", "rate"].includes(key)) return true;
      return !isValueEmpty(rowData[key]);
    });
  };

  const addRow = () => {
    if (formDataList.length >= maxItems) return;
    const currentRow = formDataList[formDataList.length - 1];
    const isRowEmpty = fields.every(({ key }) => isValueEmpty(currentRow[key]));

    if (isRowEmpty) {
      // Mark all fields as invalid to show error
      const errors: { [key: string]: boolean } = {};
      fields.forEach(({ key }) => {
        errors[key] = true;
      });

      const updatedErrors = [...formErrorsList];
      updatedErrors[formDataList.length - 1] = errors;
      setFormErrorsList(updatedErrors);

      return;
    }

    if (isCurrentRowValid(currentRow)) {
      const newFormData = generateInitialForm();
      if (
        isCreated &&
        formDataList.length > 0 &&
        formDataList[0].fluidOrderId
      ) {
        newFormData.fluidOrderId = formDataList[0].fluidOrderId;
      }
      setFormDataList((prev) => [...prev, newFormData]);
      setOffsetList((prev) => [...prev, undefined]);
      setOffsetErrors((prev) => [...prev, ""]);
      setFormErrorsList((prev) => [...prev, {}]);
    } else {
      // Show validation errors for current row
      const errors: { [key: string]: boolean } = {};
      fields.forEach(({ key }) => {
        if (
          !["additiveAmount", "rate"].includes(key) &&
          isValueEmpty(currentRow[key])
        ) {
          errors[key] = true;
        }
      });
      const updatedErrors = [...formErrorsList];
      updatedErrors[formDataList.length - 1] = errors;
      setFormErrorsList(updatedErrors);
    }
  };

  const deleteRow = (index: number) => {
    setFormDataList((prev) => prev.filter((_: any, i: any) => i !== index));
    setOffsetList((prev) => prev.filter((_, i: any) => i !== index));
    setOffsetErrors((prev) => prev.filter((_, i: any) => i !== index));
    setFormErrorsList((prev) => prev.filter((_, i: any) => i !== index));
  };

  const onCancel = () => {
    if (isEditMode && originalData) {
      setInstructions(originalData.instructions);
      setFluidRestriction(originalData.fluidRestriction);
      setNilSelected(originalData.isNilRestriction);
      setComorbidities(originalData.comorbidities);
      setOtherComborbidity(originalData.otherDescription);
      setFormDataList(JSON.parse(JSON.stringify(originalData.fluidOrderItems))); // deep clone again
      setOffsetList([...originalData.offsetList]);
      setOffsetErrors(new Array(originalData.fluidOrderItems.length).fill(""));
      setFormErrorsList(
        new Array(originalData.fluidOrderItems.length).fill({})
      );
      setIsEditMode(
        getFluidResponse.fluidOrderItems.length === 0 ? true : false
      );
    } else {
      // Reset for new form if not editing
      setInstructions("");
      setComorbidities({
        heartFailure: false,
        kidneyFailure: false,
        liverFailure: false,
        diabetes: false,
        nil: false,
        other: false,
      });
      setOtherComborbidity("");
      setFluidRestriction("");
      setNilSelected(false);
      setFormDataList([generateInitialForm()]);
      setOffsetList([undefined]);
      setOffsetErrors([""]);
      setFormErrorsList([{}]);
    }
  };

  const handleCreate = () => {
    let hasError = false;
    const updatedErrorList: Array<{ [key: string]: boolean }> = [];
    const updatedOffsetErrors: string[] = [];

    formDataList.forEach((formData: any, index: any) => {
      const errors: { [key: string]: boolean } = {};

      //validate fields for reach row
      fields.forEach(({ key }) => {
        if (
          !["additiveAmount", "rate"].includes(key) &&
          isValueEmpty(formData[key])
        ) {
          errors[key] = true;
          hasError = true;
        }
      });

      if (Object.keys(errors).length > 0) {
        updatedErrorList[index] = errors;
      } else {
        updatedErrorList[index] = {}; // Clear the error for valid fields
      }
      if (offsetList[index] === 0) {
        hasError = true;
        updatedOffsetErrors[index] = "Please enter valid input.";
      }
    });
    const hasValue = updatedErrorList.some(
      (error) => Object.keys(error).length > 0
    );
    if (hasError && hasValue) {
      setFormErrorsList(updatedErrorList);
      return;
    }
    if (hasError && updatedOffsetErrors.length) {
      setOffsetErrors(updatedOffsetErrors);
      return;
    }

    if (!hasError) {
      const finalPayload = {
        templateId,
        instructions: instructions,
        isHeartFailure: comorbidities.heartFailure,
        isKidneyFailure: comorbidities.kidneyFailure,
        isLiverFailure: comorbidities.liverFailure,
        isDiabetes: comorbidities.diabetes,
        isNil: comorbidities.nil,
        other: comorbidities.other,
        otherDescription: otherComborbidity,
        fluidRestriction,
        isNilRestriction: nilSelected,

        fluidOrderItems: formDataList.map((item: any, index: number) => {
          const itemPayload: any = {
            indication: item.indication,
            fluidType: item.fluidType,
            volume: item.volume,
            additiveAmount: item.additiveAmount,
            rate: item.rate,
            route: item.route,
          };
          const currentUserPrescriber = CommonFunctions.formatFirstAndLastName(
            auth?.userInfo?.userFirstName,
            auth?.userInfo?.userLastName,
            { prefixKey: "doctor" }
          );
          const originalItem = originalData?.fluidOrderItems?.[index];
          const originalOffset = originalData?.offsetList?.[index];

          const hasFieldChanged =
            !originalItem ||
            fields.some(({ key }) => {
              return (
                item[key]?.toString().trim() !==
                originalItem[key]?.toString().trim()
              );
            });

          const hasOffsetChanged = offsetList[index] !== originalOffset;
          const hasChanged = hasFieldChanged || hasOffsetChanged;
          itemPayload.prescribedBy = hasChanged
            ? currentUserPrescriber
            : item.prescribedBy;

          const offsetValue = offsetList[index];
          if (typeof offsetValue === "number" && offsetValue !== 0) {
            itemPayload.offset = offsetValue;
          }
          if (isCreated && item.fluidOrderId) {
            itemPayload.fluidOrderId = item.fluidOrderId;
          }

          return itemPayload;
        }),
      };

      const message = `Adult Fluid Order has been Successfully ${
        isCreated ? "Updated." : "Created."
      }`;
      if (!isCreated) {
        postService(
          `${apiEndpoints.TEMPLATE_FLUID_ORDER}`,
          finalPayload,
          message
        );
      } else {
        putService(
          `${apiEndpoints.TEMPLATE_FLUID_ORDER}`,
          finalPayload,
          message
        );
      }
    }
  };

  useEffect(() => {
    getFluidLoading && dispatch(showLoader());
    !getFluidLoading && dispatch(hideLoader());
  }, [getFluidLoading]);

  useEffect(() => {
    if (putRes || postRes) {
      getService(`${apiEndpoints.TEMPLATE_FLUID_ORDERS}/${templateId}`);
    }
  }, [putRes, postRes]);

  useEffect(() => {
    if (templateId && !getFluidResponse) {
      getService(`${apiEndpoints.TEMPLATE_FLUID_ORDERS}/${templateId}`);
    }

    if (getFluidResponse) {
      const data = getFluidResponse;

      const fluidItems =
        data.fluidOrderItems.length > 0
          ? data.fluidOrderItems
          : [generateInitialForm()];

      const offsets = data.fluidOrderItems.map((item: any) =>
        item.offset === 0 ? undefined : item.offset
      );

      setInstructions(data.instructions);
      setFluidRestriction(data.fluidRestriction);
      setNilSelected(data.isNilRestriction);
      setComorbidities({
        heartFailure: data.isHeartFailure,
        kidneyFailure: data.isKidneyFailure,
        liverFailure: data.isLiverFailure,
        diabetes: data.isDiabetes,
        nil: data.isNil,
        other: data.other,
      });
      setOtherComborbidity(data.otherDescription || "");
      setFormDataList(fluidItems);
      setOffsetList(offsets);
      setOffsetErrors(new Array(fluidItems.length).fill(""));
      setFormErrorsList(new Array(fluidItems.length).fill({}));

      // Deep clone to avoid mutation
      const clonedFluidItems = JSON.parse(JSON.stringify(fluidItems));
      const clonedOffsets = JSON.parse(JSON.stringify(offsets));

      setOriginalData({
        instructions: data.instructions,
        fluidRestriction: data.fluidRestriction,
        isNilRestriction: data.isNilRestriction,
        comorbidities: {
          heartFailure: data.isHeartFailure,
          kidneyFailure: data.isKidneyFailure,
          liverFailure: data.isLiverFailure,
          diabetes: data.isDiabetes,
          nil: data.isNil,
          other: data.other,
        },
        otherDescription: data.otherDescription || "",
        fluidOrderItems: clonedFluidItems,
        offsetList: clonedOffsets,
      });
      setIsEditMode(data.fluidOrderItems.length === 0 ? true : false);
    }
  }, [templateId, getFluidResponse]);

  const handleComborbidityChange = (key: string) => {
    setComorbidities((prev: any) => {
      const updated = { ...prev, [key]: !prev[key] };

      if (key === "other" && !updated[key]) {
        setOtherComborbidity("");
      }
      return updated;
    });
  };

  const handleInstructionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstructions(e.target.value);
  };

  const handleFluidRestrictionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFluidRestriction(e.target.value);
  };

  const handleOtherCombodityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOtherComborbidity(e.target.value);
  };
  const handleNilToggle = () => {
    setNilSelected((prev) => !prev);
    setFluidRestriction("");
  };

  const onClickEditMode = () => {
    setIsEditMode(!isEditMode);
    setIsCreated(true);
  };

  return (
    <Grid2 sx={{ ml: "20px" }}>
      <Box sx={style}>
        <Input
          name="instructions"
          label="Instructions"
          value={instructions}
          size="small"
          onChange={handleInstructionsChange}
          inputProps={{ maxLength: 250 }}
          minRows={1}
          maxRows={3}
          multiline={true}
          disabled={!isEditMode || isCoordinate}
        />
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: "17px",
          }}
        >
          <Heading
            text="Relevant Comorbidities"
            isBold
            style={{
              opacity: isEditMode && !isCoordinate ? 1 : 0.5,
            }}
          />
        </span>

        <Grid2 container>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {comorbiditesList.map((item: any) => (
              <Box
                key={item.key}
                sx={{
                  whiteSpace: "nowrap", // Keep label on one line
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CheckboxInput
                  style={{
                    transform: "scale(1.3)",
                  }}
                  key={item.key}
                  label={item.label}
                  value={comorbidities[item.key]}
                  onChange={() => handleComborbidityChange(item.key)}
                  disabled={!isEditMode || isCoordinate}
                />
              </Box>
            ))}
            <Box
              sx={{
                flex: "1 1 auto",
                minWidth: "150px",
                maxWidth: { xs: "100%", sm: "50%" },
                display: "flex",
                alignItems: "center",
              }}
            >
              <Input
                label="Other"
                value={otherComborbidity}
                name="otherComborbidity"
                size="small"
                disabled={!comorbidities.other || !isEditMode}
                inputProps={{ maxLength: 50 }}
                onChange={handleOtherCombodityChange}
              />
            </Box>
          </Box>
        </Grid2>
        <Heading
          text="Fluid Restriction"
          isBold
          style={{
            marginTop: "5px",
            marginBottom: "8px",
            opacity: isEditMode && !isCoordinate ? 1 : 0.5,
          }}
        />
        <Grid2 container spacing={2}>
          <Grid2 size={5}>
            <Input
              label="Fluid Restriction mL/24 hrs"
              value={fluidRestriction}
              name="fluidRestriction"
              size="small"
              onChange={handleFluidRestrictionChange}
              disabled={nilSelected || !isEditMode || isCoordinate}
            />

            <Heading
              text="*Consider checking bloods / electrolytes where applicable"
              style={{
                fontSize: "0.75rem",
                opacity: isEditMode && !isCoordinate ? 1 : 0.5,
              }}
            />
          </Grid2>
          <Grid2 size={3}>
            <CheckboxInput
              label="Nil"
              value={nilSelected}
              onChange={handleNilToggle}
              style={{
                transform: "scale(1.3)",
              }}
              disabled={!isEditMode || isCoordinate}
            />
          </Grid2>
        </Grid2>
        <FluidOrderItem
          fields={fields}
          maxItems={10}
          formDataList={formDataList}
          setFormDataList={setFormDataList}
          addRow={addRow}
          deleteRow={deleteRow}
          onCancel={onCancel}
          handleCreate={handleCreate}
          offsetList={offsetList}
          setOffsetList={setOffsetList}
          isEditMode={isEditMode}
          onClickEditMode={onClickEditMode}
          offsetErrors={offsetErrors}
          setOffsetErrors={setOffsetErrors}
          formErrorsList={formErrorsList}
          setFormErrorsList={setFormErrorsList}
          isCreated={isCreated}
        />
      </Box>
    </Grid2>
  );
};
